/** @format */

import {
  ProgramAttentionCategory,
  ProgramAttentionActionQuery,
  ProgramAttentionPreview,
  ProgramAttentionSendResult,
} from "@domain/program/program-attention-actions.model";
/** @format */

import { HttpParams } from "@angular/common/http";
import {
  ProgramCaseQuery,
  ProgramCaseFilters,
  ProgramCaseSelection,
} from "@domain/program/program-case-analytics.model";
import { ProgramRoleWidget } from "@domain/program/program-role-widget.model";
import { inject, Injectable } from "@angular/core";
import { map, Observable, tap, throwError } from "rxjs";
import { User } from "@domain/auth/user.model";
import { ApiPagination } from "@domain/other/api-pagination.model";
import { PartnerProgramFields } from "@domain/program/partner-program-fields.model";
import { ProgramCreate } from "@domain/program/program-create.model";
import { Program, ProgramDataSchema } from "@domain/program/program.model";
import { ProjectAdditionalFields } from "@domain/project/project-additional-fields.model";
import { Project } from "@domain/project/project.model";
import { ProgramHttpAdapter } from "../../adapters/program/program-http.adapter";
import { ProgramRepositoryPort } from "@domain/program/ports/program.repository.port";
import { EntityCache } from "@domain/shared/entity-cache";
import { ApplyToProgramDTO } from "@domain/program/dto/apply-to-program.model";
import { ApplyToProgramResponse } from "@domain/program/results/apply-to-program";
import { userFromRaw } from "@utils/userRaw";
import { EventBus } from "@domain/shared/event-bus";
import { LoggedOut } from "@domain/auth/events/logged-out.event";
import { ProgramUpdated } from "@domain/program/program-updated.event";
import {
  ProgramAnalyticsAttentionPage,
  ProgramAnalyticsAttentionParticipant,
  ProgramAnalyticsAttentionProjects,
  ProgramAnalyticsNotSubmittedProjectsPage,
  ProgramAnalyticsAttentionQuery,
} from "@domain/program/program-analytics-attention.model";
import {
  ProgramAnalyticsOverview,
  ProgramAnalyticsAssignment,
  ProgramAnalyticsAssignmentScope,
  ProgramAnalyticsAssignmentScoreDetail,
} from "@domain/program/program-analytics.model";

/** Репозиторий программ: `EntityCache<Program>` для `getOne`, остальное — passthrough. */
@Injectable({ providedIn: "root" })
export class ProgramRepository implements ProgramRepositoryPort {
  previewAttentionReminder(
    programId: number,
    category: ProgramAttentionCategory,
    query: ProgramAttentionActionQuery,
  ): Observable<ProgramAttentionPreview> {
    return this.programAdapter.previewAttentionReminder(programId, category, query);
  }
  sendAttentionReminder(
    programId: number,
    category: ProgramAttentionCategory,
    token: string,
  ): Observable<ProgramAttentionSendResult> {
    return this.programAdapter.sendAttentionReminder(programId, category, token);
  }
  exportAttention(
    programId: number,
    category: ProgramAttentionCategory,
    query: ProgramAttentionActionQuery,
  ): Observable<Blob> {
    return this.programAdapter.exportAttention(programId, category, query);
  }

  getCaseProjects(programId: number, query: ProgramCaseQuery): Observable<unknown> {
    return this.programAdapter.getCaseProjects(programId, query);
  }

  exportCaseProjects(
    programId: number,
    selection: ProgramCaseSelection,
    filters?: ProgramCaseFilters,
  ): Observable<Blob> {
    return this.programAdapter.exportCaseProjects(programId, selection, filters);
  }

  getRoleWidget(programId: number): Observable<ProgramRoleWidget> {
    return this.programAdapter.getRoleWidget(programId);
  }
  private readonly programAdapter = inject(ProgramHttpAdapter);
  private readonly entityCache = new EntityCache<Program>(5 * 60 * 1000);
  private readonly eventBus = inject(EventBus);

  constructor() {
    this.initializeEvents();
  }

  private initializeEvents(): void {
    this.eventBus
      .on<ProgramUpdated>("ProgramUpdated")
      .subscribe(event => this.entityCache.invalidate(event.payload.programId));
    this.eventBus.on<LoggedOut>("LoggedOut").subscribe({
      next: () => {
        this.entityCache.clear();
      },
    });
  }

  getAll(skip: number, take: number, params?: HttpParams): Observable<ApiPagination<Program>> {
    return this.programAdapter.getAll(skip, take, params);
  }

  getOne(programId: number): Observable<Program> {
    const id = this.normalizeProgramId(programId);
    if (id === null) return throwError(() => new RangeError("Invalid program id"));
    return this.entityCache.getOrFetch(id, () => this.programAdapter.getOne(id));
  }

  getManagerOverview(programId: number): Observable<ProgramAnalyticsOverview> {
    return this.programAdapter.getManagerOverview(programId);
  }

  /** Свежий manager-список при каждом открытии/поиске, без EntityCache. */
  getManagerParticipantsWithoutVisit(
    programId: number,
    query: ProgramAnalyticsAttentionQuery,
  ): Observable<ProgramAnalyticsAttentionPage<ProgramAnalyticsAttentionParticipant>> {
    return this.programAdapter.getManagerParticipantsWithoutVisit(programId, query);
  }

  /** Свежий manager-список при каждом открытии/поиске, без EntityCache. */
  getManagerParticipantsWithoutTeam(
    programId: number,
    query: ProgramAnalyticsAttentionQuery,
  ): Observable<ProgramAnalyticsAttentionPage<ProgramAnalyticsAttentionParticipant>> {
    return this.programAdapter.getManagerParticipantsWithoutTeam(programId, query);
  }

  /** Проксируем актуальный count/mode и nullable поля без вычислений. */
  getManagerProjectsAwaitingEvaluation(
    programId: number,
    query: ProgramAnalyticsAttentionQuery,
  ): Observable<ProgramAnalyticsAttentionProjects> {
    return this.programAdapter.getManagerProjectsAwaitingEvaluation(programId, query);
  }

  /** Передаёт страницу несданных связей без кэша, нормализации или пересчёта срока. */
  getManagerProjectsNotSubmitted(
    programId: number,
    query: ProgramAnalyticsAttentionQuery,
  ): Observable<ProgramAnalyticsNotSubmittedProjectsPage> {
    return this.programAdapter.getManagerProjectsNotSubmitted(programId, query);
  }

  /** Не кешируем manager-данные между открытиями или программами. */
  getManagerAssignments(
    programId: number,
    scope: ProgramAnalyticsAssignmentScope,
  ): Observable<ProgramAnalyticsAssignment[]> {
    return this.programAdapter.getManagerAssignments(programId, scope);
  }

  /** Возвращает все критерии, включая ещё не оценённые. */
  getManagerAssignmentScores(
    programId: number,
    assignmentId: number,
  ): Observable<ProgramAnalyticsAssignmentScoreDetail> {
    return this.programAdapter.getManagerAssignmentScores(programId, assignmentId);
  }

  acknowledgeWelcome(programId: number): Observable<{ welcomeAcknowledgedAt: string }> {
    const id = this.normalizeProgramId(programId);
    if (id === null) return throwError(() => new RangeError("Invalid program id"));
    return this.programAdapter
      .acknowledgeWelcome(id)
      .pipe(tap(() => this.entityCache.invalidate(id)));
  }

  /** Route callers may pass strings at runtime. Detail cache reads and welcome
   * invalidation must use the same numeric key; invalid IDs become Observable errors.
   */
  private normalizeProgramId(programId: number): number | null {
    const id = Number(programId);
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  create(program: ProgramCreate): Observable<Program> {
    return this.programAdapter.create(program);
  }

  getDataSchema(programId: number): Observable<ProgramDataSchema> {
    return this.programAdapter.getDataSchema(programId).pipe(map(response => response.dataSchema));
  }

  register(
    programId: number,
    additionalData: Record<string, string>,
  ): Observable<ProgramDataSchema> {
    return this.programAdapter.register(programId, additionalData);
  }

  getAllProjects(programId: number, params?: HttpParams): Observable<ApiPagination<Project>> {
    return this.programAdapter.getAllProjects(programId, params);
  }

  getPublishedProjects(programId: number, params?: HttpParams): Observable<ApiPagination<Project>> {
    return this.programAdapter.getPublishedProjects(programId, params);
  }

  getAllMembers(programId: number, skip: number, take: number): Observable<ApiPagination<User>> {
    return this.programAdapter.getAllMembers(programId, skip, take).pipe(
      map(result => ({
        ...result,
        results: result.results.map(user => userFromRaw(user)),
      })),
    );
  }

  getProgramFilters(programId: number): Observable<PartnerProgramFields[]> {
    return this.programAdapter.getProgramFilters(programId);
  }

  getProgramProjectAdditionalFields(programId: number): Observable<ProjectAdditionalFields> {
    return this.programAdapter.getProgramProjectAdditionalFields(programId);
  }

  applyProjectToProgram(
    programId: number,
    dto: ApplyToProgramDTO,
  ): Observable<ApplyToProgramResponse> {
    return this.programAdapter
      .applyProjectToProgram(programId, dto)
      .pipe(tap(() => this.entityCache.invalidate(programId)));
  }

  createProgramFilters(
    programId: number,
    filters: Record<string, string[]>,
    params?: HttpParams,
  ): Observable<ApiPagination<Project>> {
    return this.programAdapter.createProgramFilters(programId, filters, params);
  }

  submitCompettetiveProject(relationId: number): Observable<Project> {
    return this.programAdapter.submitCompettetiveProject(relationId);
  }
}
